'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import {
  Plus,
  Pencil,
  Trash2,
  Building2,
  UserCog,
  Upload,
  RefreshCw,
  Search,
  Users,
  AlertTriangle,
  CheckCircle2,
  FileUp,
  ClipboardPaste,
  FileDown,
  FileJson,
  ChevronLeft,
  ChevronRight,
  Eye,
  Info,
} from 'lucide-react';

// ─── Types ──────────────────────────────────────────────────────────────────

interface BoothData {
  id: string;
  pollingBoothId: string;
  name: string;
  address: string;
  district: string;
  constituency: string;
  constituencyNo: string;
  wardNo: string;
  totalVoters: number;
  officerCount: number;
  voterCount: number;
  createdAt: string;
  updatedAt: string;
}

interface OfficerData {
  id: string;
  username: string;
  name: string;
  mobile: string;
  email: string;
  pollingBoothId: string;
  isActive: boolean;
  boothName: string;
  createdAt: string;
  updatedAt: string;
}

interface VoterListItem {
  id: string;
  epicNo: string;
  serialNo: number;
  name: string;
  nameHindi: string;
  fatherHusbandName: string;
  relationType: string;
  age: number;
  gender: string;
  address: string;
  houseNo: string;
  pollingBoothId: string;
}

// ─── Auth Headers Helper ────────────────────────────────────────────────────

function getAuthHeaders(): Record<string, string> {
  const token = useAuthStore.getState().token;
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

// ─── CSV Parser ─────────────────────────────────────────────────────────────

const parseCSV = (text: string) => {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return [];
  const headers = lines[0].split(',');
  return lines.slice(1).map((line) => {
    const values = line.split(',');
    const obj: Record<string, string> = {};
    headers.forEach((h, i) => {
      obj[h.trim()] = values[i]?.trim() || '';
    });
    return obj;
  });
};

// ─── Gender Hindi Mapping ───────────────────────────────────────────────────

const genderInHindi = (gender: string): string => {
  const lower = gender?.toLowerCase() || '';
  if (lower === 'male' || lower === 'पुरुष' || lower === 'm') return 'पुरुष';
  if (lower === 'female' || lower === 'महिला' || lower === 'f') return 'महिला';
  return 'अन्य';
};

// ─── Main Component ─────────────────────────────────────────────────────────

export function AdminDashboard() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('booths');

  // ─── Booths State ───────────────────────────────────────────────────────
  const [booths, setBooths] = useState<BoothData[]>([]);
  const [boothsLoading, setBoothsLoading] = useState(false);
  const [boothDialogOpen, setBoothDialogOpen] = useState(false);
  const [boothEditId, setBoothEditId] = useState<string | null>(null);
  const [boothForm, setBoothForm] = useState({
    pollingBoothId: '',
    name: '',
    address: '',
    district: '',
    constituency: '',
    constituencyNo: '',
    wardNo: '',
  });
  const [boothSearch, setBoothSearch] = useState('');

  // ─── Officers State ─────────────────────────────────────────────────────
  const [officers, setOfficers] = useState<OfficerData[]>([]);
  const [officersLoading, setOfficersLoading] = useState(false);
  const [officerDialogOpen, setOfficerDialogOpen] = useState(false);
  const [officerEditId, setOfficerEditId] = useState<string | null>(null);
  const [officerForm, setOfficerForm] = useState({
    username: '',
    password: '',
    name: '',
    mobile: '',
    email: '',
    pollingBoothId: '',
  });
  const [officerSearch, setOfficerSearch] = useState('');

  // ─── Voter Upload State ─────────────────────────────────────────────────
  const [uploadBoothId, setUploadBoothId] = useState('');
  const [uploadMode, setUploadMode] = useState<'file' | 'json'>('file');
  const [jsonText, setJsonText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState<{ success: boolean; message: string } | null>(null);

  // ─── Voter List State (for viewing voters in table) ─────────────────────
  const [voterListBoothId, setVoterListBoothId] = useState('');
  const [voterListData, setVoterListData] = useState<VoterListItem[]>([]);
  const [voterListTotal, setVoterListTotal] = useState(0);
  const [voterListPage, setVoterListPage] = useState(1);
  const [voterListTotalPages, setVoterListTotalPages] = useState(0);
  const [voterListSearch, setVoterListSearch] = useState('');
  const [voterListLoading, setVoterListLoading] = useState(false);

  // ─── Sync Settings State ──────────────────────────────────────────────
  const [globalSyncEnabled, setGlobalSyncEnabled] = useState(true);
  const [globalSyncFreq, setGlobalSyncFreq] = useState(30);
  const [boothSyncOverrides, setBoothSyncOverrides] = useState<Record<string, { enabled: boolean; freq: number }>>({});
  const [syncSettingsLoading, setSyncSettingsLoading] = useState(false);
  const [editingBoothSync, setEditingBoothSync] = useState<string | null>(null);

  // ─── Fetch Booths ───────────────────────────────────────────────────────
  const fetchBooths = useCallback(async () => {
    setBoothsLoading(true);
    try {
      const res = await fetch('/api/admin/booths', { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) {
        setBooths(data.booths);
      } else {
        toast({ title: 'त्रुटि', description: data.error || 'मतदान कक्ष लाने में विफल', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'त्रुटि', description: 'सर्वर से कनेक्ट नहीं हो सका', variant: 'destructive' });
    } finally {
      setBoothsLoading(false);
    }
  }, [toast]);

  // ─── Fetch Officers ─────────────────────────────────────────────────────
  const fetchOfficers = useCallback(async () => {
    setOfficersLoading(true);
    try {
      const res = await fetch('/api/admin/officers', { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) {
        setOfficers(data.officers);
      } else {
        toast({ title: 'त्रुटि', description: data.error || 'अधिकारी लाने में विफल', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'त्रुटि', description: 'सर्वर से कनेक्ट नहीं हो सका', variant: 'destructive' });
    } finally {
      setOfficersLoading(false);
    }
  }, [toast]);

  // ─── Fetch Voter List ───────────────────────────────────────────────────
  const fetchVoterList = useCallback(async (boothId: string, page: number = 1, search: string = '') => {
    if (!boothId) return;
    setVoterListLoading(true);
    try {
      const params = new URLSearchParams({
        boothId,
        page: String(page),
        limit: '25',
        search,
      });
      const res = await fetch(`/api/admin/voters/list?${params.toString()}`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) {
        setVoterListData(data.voters);
        setVoterListTotal(data.total);
        setVoterListPage(data.page);
        setVoterListTotalPages(data.totalPages);
      } else {
        toast({ title: 'त्रुटि', description: data.error || 'मतदाता सूची लाने में विफल', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'त्रुटि', description: 'सर्वर से कनेक्ट नहीं हो सका', variant: 'destructive' });
    } finally {
      setVoterListLoading(false);
    }
  }, [toast]);

  // ─── Fetch Sync Settings ─────────────────────────────────────────────
  const fetchSyncSettings = useCallback(async () => {
    setSyncSettingsLoading(true);
    try {
      const res = await fetch('/api/admin/sync-settings', { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success && data.settings) {
        setGlobalSyncEnabled(data.settings.syncEnabled);
        setGlobalSyncFreq(data.settings.syncFrequencyMinutes);
      }
      // Fetch booth-level overrides
      if (booths.length > 0) {
        const overrides: Record<string, { enabled: boolean; freq: number }> = {};
        await Promise.all(
          booths.map(async (booth) => {
            try {
              const bRes = await fetch(`/api/admin/sync-settings/${booth.pollingBoothId}`, { headers: getAuthHeaders() });
              const bData = await bRes.json();
              if (bData.success && bData.settings) {
                overrides[booth.pollingBoothId] = {
                  enabled: bData.settings.boothSyncEnabled ?? true,
                  freq: bData.settings.effectiveFrequencyMinutes ?? data.settings?.syncFrequencyMinutes ?? 30,
                };
              }
            } catch { /* silent */ }
          })
        );
        setBoothSyncOverrides(overrides);
      }
    } catch {
      // silent
    } finally {
      setSyncSettingsLoading(false);
    }
  }, [booths]);

  // ─── Save Global Sync Settings ───────────────────────────────────────
  const saveGlobalSyncSettings = async () => {
    try {
      const res = await fetch('/api/admin/sync-settings', {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ syncEnabled: globalSyncEnabled, syncFrequencyMinutes: globalSyncFreq }),
      });
      const data = await res.json();
      if (data.success) {
        toast({ title: 'सफल', description: 'सिंक सेटिंग्स अपडेट हो गईं' });
        fetchSyncSettings();
      } else {
        toast({ title: 'त्रुटि', description: data.error || 'सिंक सेटिंग्स अपडेट विफल', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'त्रुटि', description: 'सिंक सेटिंग्स अपडेट विफल', variant: 'destructive' });
    }
  };

  // ─── Save Booth Sync Settings ────────────────────────────────────────
  const saveBoothSyncSettings = async (boothId: string, enabled: boolean, freq?: number) => {
    try {
      const res = await fetch(`/api/admin/sync-settings/${boothId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ syncEnabled: enabled, syncFrequencyMinutes: freq, boothSyncEnabled: enabled }),
      });
      const data = await res.json();
      if (data.success) {
        toast({ title: 'सफल', description: 'बूथ सिंक सेटिंग्स अपडेट हो गईं' });
        fetchSyncSettings();
      } else {
        toast({ title: 'त्रुटि', description: data.error || 'बूथ सिंक सेटिंग्स अपडेट विफल', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'त्रुटि', description: 'बूथ सिंक सेटिंग्स अपडेट विफल', variant: 'destructive' });
    }
  };

  // ─── Initial Data Load ──────────────────────────────────────────────────
  useEffect(() => {
    fetchBooths();
    fetchOfficers();
  }, [fetchBooths, fetchOfficers]);

  // ─── Fetch sync settings when booths are loaded ──────────────────────
  useEffect(() => {
    if (booths.length > 0) {
      fetchSyncSettings();
    }
  }, [booths.length, fetchSyncSettings]);

  // ─── Auto-load voters when booth selection changes ──────────────────────
  useEffect(() => {
    if (voterListBoothId) {
      setVoterListPage(1);
      setVoterListSearch('');
      fetchVoterList(voterListBoothId, 1, '');
    } else {
      setVoterListData([]);
      setVoterListTotal(0);
      setVoterListTotalPages(0);
    }
  }, [voterListBoothId, fetchVoterList]);

  // ─── Debounced search for voter list ────────────────────────────────────
  useEffect(() => {
    if (!voterListBoothId) return;
    const timer = setTimeout(() => {
      setVoterListPage(1);
      fetchVoterList(voterListBoothId, 1, voterListSearch);
    }, 400);
    return () => clearTimeout(timer);
  }, [voterListSearch, voterListBoothId, fetchVoterList]);

  // ─── Handle voter list page change ──────────────────────────────────────
  const handleVoterListPageChange = (newPage: number) => {
    if (newPage < 1 || newPage > voterListTotalPages) return;
    setVoterListPage(newPage);
    fetchVoterList(voterListBoothId, newPage, voterListSearch);
  };

  // ─── Booth CRUD ─────────────────────────────────────────────────────────
  const resetBoothForm = () => {
    setBoothForm({
      pollingBoothId: '',
      name: '',
      address: '',
      district: '',
      constituency: '',
      constituencyNo: '',
      wardNo: '',
    });
    setBoothEditId(null);
  };

  const openAddBooth = () => {
    resetBoothForm();
    setBoothDialogOpen(true);
  };

  const openEditBooth = (booth: BoothData) => {
    setBoothEditId(booth.id);
    setBoothForm({
      pollingBoothId: booth.pollingBoothId,
      name: booth.name,
      address: booth.address,
      district: booth.district,
      constituency: booth.constituency,
      constituencyNo: booth.constituencyNo,
      wardNo: booth.wardNo,
    });
    setBoothDialogOpen(true);
  };

  const saveBooth = async () => {
    if (!boothForm.pollingBoothId || !boothForm.name) {
      toast({ title: 'त्रुटि', description: 'बूथ ID और नाम आवश्यक हैं', variant: 'destructive' });
      return;
    }

    try {
      if (boothEditId) {
        // Update
        const res = await fetch(`/api/admin/booths/${boothEditId}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            name: boothForm.name,
            address: boothForm.address,
            district: boothForm.district,
            constituency: boothForm.constituency,
            constituencyNo: boothForm.constituencyNo,
            wardNo: boothForm.wardNo,
          }),
        });
        const data = await res.json();
        if (data.success) {
          // Save booth sync settings if changed
          const syncOverride = boothSyncOverrides[boothForm.pollingBoothId];
          if (syncOverride !== undefined) {
            await saveBoothSyncSettings(boothForm.pollingBoothId, syncOverride.enabled, syncOverride.freq);
          }
          toast({ title: 'सफल', description: 'मतदान कक्ष अपडेट हुआ' });
          setBoothDialogOpen(false);
          fetchBooths();
        } else {
          toast({ title: 'त्रुटि', description: data.error, variant: 'destructive' });
        }
      } else {
        // Create
        const res = await fetch('/api/admin/booths', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(boothForm),
        });
        const data = await res.json();
        if (data.success) {
          toast({ title: 'सफल', description: 'मतदान कक्ष बनाया गया' });
          setBoothDialogOpen(false);
          fetchBooths();
        } else {
          toast({ title: 'त्रुटि', description: data.error, variant: 'destructive' });
        }
      }
    } catch {
      toast({ title: 'त्रुटि', description: 'सर्वर से कनेक्ट नहीं हो सका', variant: 'destructive' });
    }
  };

  const deleteBooth = async (id: string, name: string) => {
    if (!confirm(`क्या आप "${name}" मतदान कक्ष हटाना चाहते हैं? यह सभी संबंधित डेटा हटा देगा।`)) return;

    try {
      const res = await fetch(`/api/admin/booths/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.success) {
        toast({ title: 'सफल', description: 'मतदान कक्ष हटाया गया' });
        fetchBooths();
        fetchOfficers();
      } else {
        toast({ title: 'त्रुटि', description: data.error, variant: 'destructive' });
      }
    } catch {
      toast({ title: 'त्रुटि', description: 'सर्वर से कनेक्ट नहीं हो सका', variant: 'destructive' });
    }
  };

  // ─── Officer CRUD ───────────────────────────────────────────────────────
  const resetOfficerForm = () => {
    setOfficerForm({
      username: '',
      password: '',
      name: '',
      mobile: '',
      email: '',
      pollingBoothId: '',
    });
    setOfficerEditId(null);
  };

  const openAddOfficer = () => {
    resetOfficerForm();
    setOfficerDialogOpen(true);
  };

  const openEditOfficer = (officer: OfficerData) => {
    setOfficerEditId(officer.id);
    setOfficerForm({
      username: officer.username,
      password: '',
      name: officer.name,
      mobile: officer.mobile,
      email: officer.email,
      pollingBoothId: officer.pollingBoothId,
    });
    setOfficerDialogOpen(true);
  };

  const saveOfficer = async () => {
    if (!officerEditId && (!officerForm.username || !officerForm.password || !officerForm.name || !officerForm.pollingBoothId)) {
      toast({ title: 'त्रुटि', description: 'सभी आवश्यक फ़ील्ड भरें (यूजरनेम, पासवर्ड, नाम, बूथ)', variant: 'destructive' });
      return;
    }
    if (officerEditId && (!officerForm.name || !officerForm.pollingBoothId)) {
      toast({ title: 'त्रुटि', description: 'नाम और बूथ आवश्यक हैं', variant: 'destructive' });
      return;
    }

    try {
      if (officerEditId) {
        const body: Record<string, string> = {
          name: officerForm.name,
          mobile: officerForm.mobile,
          email: officerForm.email,
          pollingBoothId: officerForm.pollingBoothId,
        };
        if (officerForm.password) {
          body.password = officerForm.password;
        }
        const res = await fetch(`/api/admin/officers/${officerEditId}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (data.success) {
          toast({ title: 'सफल', description: 'मतदान अधिकारी अपडेट हुआ' });
          setOfficerDialogOpen(false);
          fetchOfficers();
          fetchBooths();
        } else {
          toast({ title: 'त्रुटि', description: data.error, variant: 'destructive' });
        }
      } else {
        const res = await fetch('/api/admin/officers', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(officerForm),
        });
        const data = await res.json();
        if (data.success) {
          toast({ title: 'सफल', description: 'मतदान अधिकारी बनाया गया' });
          setOfficerDialogOpen(false);
          fetchOfficers();
          fetchBooths();
        } else {
          toast({ title: 'त्रुटि', description: data.error, variant: 'destructive' });
        }
      }
    } catch {
      toast({ title: 'त्रुटि', description: 'सर्वर से कनेक्ट नहीं हो सका', variant: 'destructive' });
    }
  };

  const deleteOfficer = async (id: string, name: string) => {
    if (!confirm(`क्या आप "${name}" अधिकारी हटाना चाहते हैं?`)) return;

    try {
      const res = await fetch(`/api/admin/officers/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.success) {
        toast({ title: 'सफल', description: 'मतदान अधिकारी हटाया गया' });
        fetchOfficers();
        fetchBooths();
      } else {
        toast({ title: 'त्रुटि', description: data.error, variant: 'destructive' });
      }
    } catch {
      toast({ title: 'त्रुटि', description: 'सर्वर से कनेक्ट नहीं हो सका', variant: 'destructive' });
    }
  };

  // ─── Voter Upload ───────────────────────────────────────────────────────
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!uploadBoothId) {
      toast({ title: 'त्रुटि', description: 'कृपया पहले मतदान कक्ष चुनें', variant: 'destructive' });
      return;
    }

    setUploading(true);
    setUploadProgress(10);
    setUploadResult(null);

    try {
      const text = await file.text();
      setUploadProgress(30);

      const parsed = parseCSV(text);
      if (parsed.length === 0) {
        toast({ title: 'त्रुटि', description: 'CSV फ़ाइल खाली है या अमान्य है', variant: 'destructive' });
        setUploading(false);
        setUploadProgress(0);
        return;
      }

      setUploadProgress(50);

      const voters = parsed.map((row) => ({
        epicNo: row.epicNo || '',
        serialNo: parseInt(row.serialNo) || 0,
        name: row.name || '',
        nameHindi: row.nameHindi || '',
        fatherHusbandName: row.fatherHusbandName || '',
        relationType: row.relationType || 'पिता',
        age: parseInt(row.age) || 0,
        gender: row.gender || '',
        address: row.address || '',
        houseNo: row.houseNo || '',
        pollingBoothId: uploadBoothId,
      }));

      // Validate required fields
      const invalidVoters = voters.filter((v) => !v.epicNo || !v.serialNo || !v.name || !v.fatherHusbandName);
      if (invalidVoters.length > 0) {
        toast({
          title: 'चेतावनी',
          description: `${invalidVoters.length} पंक्तियों में आवश्यक फ़ील्ड गायब हैं (epicNo, serialNo, name, fatherHusbandName)`,
          variant: 'destructive',
        });
      }

      const validVoters = voters.filter((v) => v.epicNo && v.serialNo && v.name && v.fatherHusbandName);
      if (validVoters.length === 0) {
        toast({ title: 'त्रुटि', description: 'कोई मान्य मतदाता डेटा नहीं मिला', variant: 'destructive' });
        setUploading(false);
        setUploadProgress(0);
        return;
      }

      setUploadProgress(70);

      const res = await fetch('/api/admin/voters/upload', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ voters: validVoters }),
      });

      setUploadProgress(90);

      const data = await res.json();
      if (data.success) {
        setUploadProgress(100);
        setUploadResult({ success: true, message: `${data.count} मतदाता सफलतापूर्वक अपलोड किए गए` });
        toast({ title: 'सफल', description: `${data.count} मतदाता अपलोड हुए` });
        fetchBooths();
        // Refresh voter list if viewing the same booth
        if (voterListBoothId === uploadBoothId) {
          fetchVoterList(voterListBoothId, voterListPage, voterListSearch);
        }
      } else {
        setUploadResult({ success: false, message: data.error || 'अपलोड विफल' });
        toast({ title: 'त्रुटि', description: data.error, variant: 'destructive' });
      }
    } catch {
      toast({ title: 'त्रुटि', description: 'फ़ाइल पढ़ने में विफल', variant: 'destructive' });
      setUploadResult({ success: false, message: 'फ़ाइल पढ़ने में विफल' });
    } finally {
      setUploading(false);
    }
  };

  const handleJsonUpload = async () => {
    if (!uploadBoothId) {
      toast({ title: 'त्रुटि', description: 'कृपया पहले मतदान कक्ष चुनें', variant: 'destructive' });
      return;
    }

    if (!jsonText.trim()) {
      toast({ title: 'त्रुटि', description: 'कृपया JSON डेटा पेस्ट करें', variant: 'destructive' });
      return;
    }

    setUploading(true);
    setUploadProgress(20);
    setUploadResult(null);

    try {
      let parsed = JSON.parse(jsonText);
      if (!Array.isArray(parsed)) {
        toast({ title: 'त्रुटि', description: 'JSON एक ऐरे होना चाहिए', variant: 'destructive' });
        setUploading(false);
        setUploadProgress(0);
        return;
      }

      setUploadProgress(50);

      const voters = parsed.map((row: Record<string, unknown>) => ({
        epicNo: String(row.epicNo || ''),
        serialNo: Number(row.serialNo) || 0,
        name: String(row.name || ''),
        nameHindi: String(row.nameHindi || ''),
        fatherHusbandName: String(row.fatherHusbandName || ''),
        relationType: String(row.relationType || 'पिता'),
        age: Number(row.age) || 0,
        gender: String(row.gender || ''),
        address: String(row.address || ''),
        houseNo: String(row.houseNo || ''),
        pollingBoothId: uploadBoothId,
      }));

      const validVoters = voters.filter(
        (v: { epicNo: string; serialNo: number; name: string; fatherHusbandName: string }) =>
          v.epicNo && v.serialNo && v.name && v.fatherHusbandName
      );

      if (validVoters.length === 0) {
        toast({ title: 'त्रुटि', description: 'कोई मान्य मतदाता डेटा नहीं मिला', variant: 'destructive' });
        setUploading(false);
        setUploadProgress(0);
        return;
      }

      setUploadProgress(70);

      const res = await fetch('/api/admin/voters/upload', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ voters: validVoters }),
      });

      setUploadProgress(90);

      const data = await res.json();
      if (data.success) {
        setUploadProgress(100);
        setUploadResult({ success: true, message: `${data.count} मतदाता सफलतापूर्वक अपलोड किए गए` });
        toast({ title: 'सफल', description: `${data.count} मतदाता अपलोड हुए` });
        fetchBooths();
        // Refresh voter list if viewing the same booth
        if (voterListBoothId === uploadBoothId) {
          fetchVoterList(voterListBoothId, voterListPage, voterListSearch);
        }
      } else {
        setUploadResult({ success: false, message: data.error || 'अपलोड विफल' });
        toast({ title: 'त्रुटि', description: data.error, variant: 'destructive' });
      }
    } catch {
      toast({ title: 'त्रुटि', description: 'अमान्य JSON फ़ॉर्मेट', variant: 'destructive' });
      setUploadResult({ success: false, message: 'अमान्य JSON फ़ॉर्मेट' });
    } finally {
      setUploading(false);
    }
  };

  // ─── Filtered Data ──────────────────────────────────────────────────────
  const filteredBooths = booths.filter(
    (b) =>
      b.pollingBoothId.toLowerCase().includes(boothSearch.toLowerCase()) ||
      b.name.toLowerCase().includes(boothSearch.toLowerCase()) ||
      b.district.toLowerCase().includes(boothSearch.toLowerCase()) ||
      b.constituency.toLowerCase().includes(boothSearch.toLowerCase())
  );

  const filteredOfficers = officers.filter(
    (o) =>
      o.username.toLowerCase().includes(officerSearch.toLowerCase()) ||
      o.name.toLowerCase().includes(officerSearch.toLowerCase()) ||
      o.mobile.toLowerCase().includes(officerSearch.toLowerCase()) ||
      o.boothName.toLowerCase().includes(officerSearch.toLowerCase())
  );

  // ─── Stats ──────────────────────────────────────────────────────────────
  const totalVoters = booths.reduce((sum, b) => sum + b.totalVoters, 0);
  const totalOfficers = officers.length;
  const activeOfficers = officers.filter((o) => o.isActive).length;

  // ─── Generate page numbers for voter list pagination ────────────────────
  const getVoterListPageNumbers = () => {
    const pages: (number | string)[] = [];
    const totalPages = voterListTotalPages;
    const currentPage = voterListPage;

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Building2 className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <p className="text-xs text-emerald-600 font-medium">मतदान कक्ष</p>
                <p className="text-2xl font-bold text-emerald-900">{booths.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Users className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <p className="text-xs text-emerald-600 font-medium">कुल मतदाता</p>
                <p className="text-2xl font-bold text-emerald-900">{totalVoters.toLocaleString('hi-IN')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <UserCog className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <p className="text-xs text-emerald-600 font-medium">कुल अधिकारी</p>
                <p className="text-2xl font-bold text-emerald-900">{totalOfficers}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <p className="text-xs text-emerald-600 font-medium">सक्रिय अधिकारी</p>
                <p className="text-2xl font-bold text-emerald-900">{activeOfficers}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4 bg-emerald-50 border border-emerald-200 h-auto p-1">
          <TabsTrigger
            value="booths"
            className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white py-2 sm:py-2.5 text-xs sm:text-sm font-medium transition-all"
          >
            <Building2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">मतदान कक्ष</span>
            <span className="sm:hidden">कक्ष</span>
          </TabsTrigger>
          <TabsTrigger
            value="officers"
            className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white py-2 sm:py-2.5 text-xs sm:text-sm font-medium transition-all"
          >
            <UserCog className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">मतदान अधिकारी</span>
            <span className="sm:hidden">अधिकारी</span>
          </TabsTrigger>
          <TabsTrigger
            value="voters"
            className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white py-2 sm:py-2.5 text-xs sm:text-sm font-medium transition-all"
          >
            <Upload className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">मतदाता डेटा अपलोड</span>
            <span className="sm:hidden">मतदाता</span>
          </TabsTrigger>
          <TabsTrigger
            value="sync-settings"
            className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white py-2 sm:py-2.5 text-xs sm:text-sm font-medium transition-all"
          >
            <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">सिंक सेटिंग्स</span>
            <span className="sm:hidden">सिंक</span>
          </TabsTrigger>
        </TabsList>

        {/* ═══════ TAB 1: Polling Booths ═══════ */}
        <TabsContent value="booths">
          <Card className="border-emerald-100">
            <CardHeader className="pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <CardTitle className="text-lg text-emerald-900 flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  मतदान कक्ष प्रबंधन
                </CardTitle>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      placeholder="खोजें..."
                      value={boothSearch}
                      onChange={(e) => setBoothSearch(e.target.value)}
                      className="pl-9 w-44 h-9 text-sm"
                    />
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchBooths} className="h-9">
                    <RefreshCw className={`w-4 h-4 ${boothsLoading ? 'animate-spin' : ''}`} />
                  </Button>
                  <Dialog open={boothDialogOpen} onOpenChange={(open) => { setBoothDialogOpen(open); if (!open) resetBoothForm(); }}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-9" onClick={openAddBooth}>
                        <Plus className="w-4 h-4 mr-1" />
                        बूथ जोड़ें
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle className="text-emerald-900">
                          {boothEditId ? 'मतदान कक्ष संपादित करें' : 'नया मतदान कक्ष जोड़ें'}
                        </DialogTitle>
                      </DialogHeader>
                      <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="boothId">बूथ ID *</Label>
                            <Input
                              id="boothId"
                              placeholder="PB001"
                              value={boothForm.pollingBoothId}
                              onChange={(e) => setBoothForm((f) => ({ ...f, pollingBoothId: e.target.value }))}
                              disabled={!!boothEditId}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="boothName">नाम *</Label>
                            <Input
                              id="boothName"
                              placeholder="प्राथमिक विद्यालय, ग्राम..."
                              value={boothForm.name}
                              onChange={(e) => setBoothForm((f) => ({ ...f, name: e.target.value }))}
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="boothAddress">पता</Label>
                          <Input
                            id="boothAddress"
                            placeholder="पूरा पता"
                            value={boothForm.address}
                            onChange={(e) => setBoothForm((f) => ({ ...f, address: e.target.value }))}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="boothDistrict">जिला</Label>
                            <Input
                              id="boothDistrict"
                              placeholder="भोपाल"
                              value={boothForm.district}
                              onChange={(e) => setBoothForm((f) => ({ ...f, district: e.target.value }))}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="boothConstituency">विधानसभा</Label>
                            <Input
                              id="boothConstituency"
                              placeholder="गोविंदपुरा"
                              value={boothForm.constituency}
                              onChange={(e) => setBoothForm((f) => ({ ...f, constituency: e.target.value }))}
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="boothConstNo">विधानसभा क्र.</Label>
                            <Input
                              id="boothConstNo"
                              placeholder="151"
                              value={boothForm.constituencyNo}
                              onChange={(e) => setBoothForm((f) => ({ ...f, constituencyNo: e.target.value }))}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="boothWardNo">वार्ड क्र.</Label>
                            <Input
                              id="boothWardNo"
                              placeholder="12"
                              value={boothForm.wardNo}
                              onChange={(e) => setBoothForm((f) => ({ ...f, wardNo: e.target.value }))}
                            />
                          </div>
                        </div>
                        {/* Sync Enabled Toggle (only when editing) */}
                        {boothEditId && (
                          <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                            <div>
                              <Label className="font-semibold text-emerald-900">सिंक सक्षम (Sync Enabled)</Label>
                              <p className="text-xs text-emerald-700 mt-0.5">इस बूथ के लिए सिंक चालू/बंद करें</p>
                            </div>
                            <Switch
                              checked={boothSyncOverrides[boothForm.pollingBoothId]?.enabled ?? globalSyncEnabled}
                              onCheckedChange={(checked) => {
                                setBoothSyncOverrides((prev) => ({
                                  ...prev,
                                  [boothForm.pollingBoothId]: {
                                    enabled: checked,
                                    freq: prev[boothForm.pollingBoothId]?.freq ?? globalSyncFreq,
                                  },
                                }));
                              }}
                              className="data-[state=checked]:bg-emerald-600"
                            />
                          </div>
                        )}
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => { setBoothDialogOpen(false); resetBoothForm(); }}>
                          रद्द करें
                        </Button>
                        <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={saveBooth}>
                          {boothEditId ? 'अपडेट करें' : 'बनाएं'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {boothsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 text-emerald-600 animate-spin" />
                  <span className="ml-2 text-emerald-700">लोड हो रहा है...</span>
                </div>
              ) : filteredBooths.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">कोई मतदान कक्ष नहीं मिला</p>
                  <p className="text-sm">नया मतदान कक्ष जोड़ने के लिए &quot;बूथ जोड़ें&quot; पर क्लिक करें</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-emerald-50 hover:bg-emerald-50">
                        <TableHead className="font-semibold text-emerald-900">बूथ ID</TableHead>
                        <TableHead className="font-semibold text-emerald-900">नाम</TableHead>
                        <TableHead className="font-semibold text-emerald-900">जिला</TableHead>
                        <TableHead className="font-semibold text-emerald-900">विधानसभा</TableHead>
                        <TableHead className="font-semibold text-emerald-900 text-center">कुल मतदाता</TableHead>
                        <TableHead className="font-semibold text-emerald-900 text-center">अधिकारी</TableHead>
                        <TableHead className="font-semibold text-emerald-900 text-right">कार्रवाई</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredBooths.map((booth) => (
                        <TableRow key={booth.id} className="hover:bg-emerald-50/50">
                          <TableCell>
                            <Badge variant="outline" className="font-mono text-xs border-emerald-300 text-emerald-700">
                              {booth.pollingBoothId}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-medium max-w-[200px] truncate">{booth.name}</TableCell>
                          <TableCell className="text-gray-600">{booth.district || '-'}</TableCell>
                          <TableCell className="text-gray-600">{booth.constituency || '-'}</TableCell>
                          <TableCell className="text-center">
                            <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                              {booth.totalVoters.toLocaleString('hi-IN')}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100">
                              {booth.officerCount}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50"
                                onClick={() => openEditBooth(booth)}
                                title="संपादित करें"
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => deleteBooth(booth.id, booth.name)}
                                title="हटाएं"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══════ TAB 2: Polling Officers ═══════ */}
        <TabsContent value="officers">
          <Card className="border-emerald-100">
            <CardHeader className="pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <CardTitle className="text-lg text-emerald-900 flex items-center gap-2">
                  <UserCog className="w-5 h-5" />
                  मतदान अधिकारी प्रबंधन
                </CardTitle>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      placeholder="खोजें..."
                      value={officerSearch}
                      onChange={(e) => setOfficerSearch(e.target.value)}
                      className="pl-9 w-44 h-9 text-sm"
                    />
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchOfficers} className="h-9">
                    <RefreshCw className={`w-4 h-4 ${officersLoading ? 'animate-spin' : ''}`} />
                  </Button>
                  <Dialog open={officerDialogOpen} onOpenChange={(open) => { setOfficerDialogOpen(open); if (!open) resetOfficerForm(); }}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-9" onClick={openAddOfficer}>
                        <Plus className="w-4 h-4 mr-1" />
                        अधिकारी जोड़ें
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle className="text-emerald-900">
                          {officerEditId ? 'मतदान अधिकारी संपादित करें' : 'नया मतदान अधिकारी जोड़ें'}
                        </DialogTitle>
                      </DialogHeader>
                      <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="officerUsername">यूजरनेम {!officerEditId && '*'}</Label>
                            <Input
                              id="officerUsername"
                              placeholder="officer001"
                              value={officerForm.username}
                              onChange={(e) => setOfficerForm((f) => ({ ...f, username: e.target.value }))}
                              disabled={!!officerEditId}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="officerPassword">पासवर्ड {!officerEditId ? '*' : '(खाली=नहीं बदलें)'}</Label>
                            <Input
                              id="officerPassword"
                              type="password"
                              placeholder="••••••"
                              value={officerForm.password}
                              onChange={(e) => setOfficerForm((f) => ({ ...f, password: e.target.value }))}
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="officerName">नाम *</Label>
                          <Input
                            id="officerName"
                            placeholder="राम कुमार शर्मा"
                            value={officerForm.name}
                            onChange={(e) => setOfficerForm((f) => ({ ...f, name: e.target.value }))}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="officerMobile">मोबाइल</Label>
                            <Input
                              id="officerMobile"
                              placeholder="98XXXXXXXX"
                              value={officerForm.mobile}
                              onChange={(e) => setOfficerForm((f) => ({ ...f, mobile: e.target.value }))}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="officerEmail">ईमेल</Label>
                            <Input
                              id="officerEmail"
                              placeholder="officer@example.com"
                              value={officerForm.email}
                              onChange={(e) => setOfficerForm((f) => ({ ...f, email: e.target.value }))}
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="officerBooth">मतदान कक्ष *</Label>
                          <Select
                            value={officerForm.pollingBoothId}
                            onValueChange={(value) => setOfficerForm((f) => ({ ...f, pollingBoothId: value }))}
                          >
                            <SelectTrigger id="officerBooth">
                              <SelectValue placeholder="मतदान कक्ष चुनें" />
                            </SelectTrigger>
                            <SelectContent>
                              {booths.map((booth) => (
                                <SelectItem key={booth.pollingBoothId} value={booth.pollingBoothId}>
                                  {booth.pollingBoothId} - {booth.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => { setOfficerDialogOpen(false); resetOfficerForm(); }}>
                          रद्द करें
                        </Button>
                        <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={saveOfficer}>
                          {officerEditId ? 'अपडेट करें' : 'बनाएं'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {officersLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 text-emerald-600 animate-spin" />
                  <span className="ml-2 text-emerald-700">लोड हो रहा है...</span>
                </div>
              ) : filteredOfficers.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <UserCog className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">कोई मतदान अधिकारी नहीं मिला</p>
                  <p className="text-sm">नया अधिकारी जोड़ने के लिए &quot;अधिकारी जोड़ें&quot; पर क्लिक करें</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-emerald-50 hover:bg-emerald-50">
                        <TableHead className="font-semibold text-emerald-900">यूजरनेम</TableHead>
                        <TableHead className="font-semibold text-emerald-900">नाम</TableHead>
                        <TableHead className="font-semibold text-emerald-900">मोबाइल</TableHead>
                        <TableHead className="font-semibold text-emerald-900">ईमेल</TableHead>
                        <TableHead className="font-semibold text-emerald-900">मतदान कक्ष</TableHead>
                        <TableHead className="font-semibold text-emerald-900 text-center">स्थिति</TableHead>
                        <TableHead className="font-semibold text-emerald-900 text-right">कार्रवाई</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredOfficers.map((officer) => (
                        <TableRow key={officer.id} className="hover:bg-emerald-50/50">
                          <TableCell>
                            <span className="font-mono text-sm text-gray-700">{officer.username}</span>
                          </TableCell>
                          <TableCell className="font-medium">{officer.name}</TableCell>
                          <TableCell className="text-gray-600 font-mono text-sm">{officer.mobile || '-'}</TableCell>
                          <TableCell className="text-gray-600 text-sm max-w-[180px] truncate">{officer.email || '-'}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="border-emerald-300 text-emerald-700 text-xs">
                              {officer.pollingBoothId}
                            </Badge>
                            <p className="text-xs text-gray-500 mt-0.5">{officer.boothName}</p>
                          </TableCell>
                          <TableCell className="text-center">
                            {officer.isActive ? (
                              <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">सक्रिय</Badge>
                            ) : (
                              <Badge className="bg-gray-100 text-gray-600 hover:bg-gray-100">निष्क्रिय</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50"
                                onClick={() => openEditOfficer(officer)}
                                title="संपादित करें"
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => deleteOfficer(officer.id, officer.name)}
                                title="हटाएं"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══════ TAB 3: Voter Data Upload ═══════ */}
        <TabsContent value="voters">
          <div className="space-y-6">

            {/* ── Section 1: Sample Template Download ── */}
            <Card className="border-emerald-100">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-emerald-900 flex items-center gap-2">
                  <FileDown className="w-5 h-5" />
                  सैंपल टेम्पलेट डाउनलोड
                  <span className="text-xs font-normal text-gray-500">(Sample Template Download)</span>
                </CardTitle>
                <CardDescription className="text-sm text-gray-600">
                  अपलोड करने से पहले सैंपल टेम्पलेट डाउनलोड करें और उसी फ़ॉर्मेट में डेटा तैयार करें
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                  {/* CSV Preview */}
                  <div className="border border-emerald-200 rounded-lg overflow-hidden">
                    <div className="bg-emerald-50 px-4 py-2.5 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileDown className="w-4 h-4 text-emerald-600" />
                        <span className="text-sm font-semibold text-emerald-800">CSV फ़ॉर्मेट</span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                        asChild
                      >
                        <a href="/samples/voters_sample.csv" download>
                          <FileDown className="w-3.5 h-3.5 mr-1" />
                          डाउनलोड
                        </a>
                      </Button>
                    </div>
                    <div className="bg-gray-50 p-3 font-mono text-xs text-gray-700 overflow-x-auto">
                      <p className="text-gray-400 mb-1"># CSV Header Row:</p>
                      <p className="whitespace-nowrap">epicNo,serialNo,name,nameHindi,fatherHusbandName,relationType,age,gender,address,houseNo</p>
                      <p className="text-gray-400 mt-2 mb-1"># Example Data Row:</p>
                      <p className="whitespace-nowrap">ABC1234567,1,राम कुमार,Ram Kumar,श्याम कुमार,पिता,45,पुरुष,ग्राम समरधा,12</p>
                    </div>
                  </div>

                  {/* JSON Preview */}
                  <div className="border border-emerald-200 rounded-lg overflow-hidden">
                    <div className="bg-emerald-50 px-4 py-2.5 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileJson className="w-4 h-4 text-emerald-600" />
                        <span className="text-sm font-semibold text-emerald-800">JSON फ़ॉर्मेट</span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                        asChild
                      >
                        <a href="/samples/voters_sample.json" download>
                          <FileJson className="w-3.5 h-3.5 mr-1" />
                          डाउनलोड
                        </a>
                      </Button>
                    </div>
                    <div className="bg-gray-50 p-3 font-mono text-xs text-gray-700 overflow-x-auto">
                      <pre className="whitespace-pre-wrap">{`[
  {
    "epicNo": "ABC1234567",
    "serialNo": 1,
    "name": "राम कुमार",
    "nameHindi": "Ram Kumar",
    "fatherHusbandName": "श्याम कुमार",
    "relationType": "पिता",
    "age": 45,
    "gender": "पुरुष",
    "address": "ग्राम समरधा",
    "houseNo": "12"
  }
]`}</pre>
                    </div>
                  </div>
                </div>

                {/* Format Specification */}
                <div className="border border-emerald-200 rounded-lg overflow-hidden">
                  <div className="bg-emerald-50 px-4 py-2.5 flex items-center gap-2">
                    <Info className="w-4 h-4 text-emerald-600" />
                    <span className="text-sm font-semibold text-emerald-800">फ़ील्ड विवरण (Field Specification)</span>
                  </div>
                  <div className="p-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-xs font-mono mt-0.5 shrink-0">epicNo</Badge>
                        <span className="text-gray-600">EPIC कार्ड नंबर <span className="text-red-500">*</span></span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-xs font-mono mt-0.5 shrink-0">serialNo</Badge>
                        <span className="text-gray-600">क्रम संख्या <span className="text-red-500">*</span></span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-xs font-mono mt-0.5 shrink-0">name</Badge>
                        <span className="text-gray-600">मतदाता का नाम <span className="text-red-500">*</span></span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100 text-xs font-mono mt-0.5 shrink-0">nameHindi</Badge>
                        <span className="text-gray-600">नाम (हिंदी)</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-xs font-mono mt-0.5 shrink-0">fatherHusbandName</Badge>
                        <span className="text-gray-600">पिता/पति का नाम <span className="text-red-500">*</span></span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100 text-xs font-mono mt-0.5 shrink-0">relationType</Badge>
                        <span className="text-gray-600">संबंध प्रकार (पिता/पति)</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100 text-xs font-mono mt-0.5 shrink-0">age</Badge>
                        <span className="text-gray-600">आयु</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100 text-xs font-mono mt-0.5 shrink-0">gender</Badge>
                        <span className="text-gray-600">लिंग (पुरुष/महिला/अन्य)</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100 text-xs font-mono mt-0.5 shrink-0">address</Badge>
                        <span className="text-gray-600">पता</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100 text-xs font-mono mt-0.5 shrink-0">houseNo</Badge>
                        <span className="text-gray-600">मकान नंबर</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-3"><span className="text-red-500">*</span> = आवश्यक फ़ील्ड (Required fields)</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ── Section 2: Upload Panel ── */}
            <div className="grid gap-6 lg:grid-cols-3">
              {/* Upload Form */}
              <div className="lg:col-span-2">
                <Card className="border-emerald-100">
                  <CardHeader>
                    <CardTitle className="text-lg text-emerald-900 flex items-center gap-2">
                      <Upload className="w-5 h-5" />
                      मतदाता डेटा अपलोड
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Booth Selection */}
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-emerald-800">मतदान कक्ष चुनें *</Label>
                      <Select value={uploadBoothId} onValueChange={setUploadBoothId}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="मतदान कक्ष चुनें" />
                        </SelectTrigger>
                        <SelectContent>
                          {booths.map((booth) => (
                            <SelectItem key={booth.pollingBoothId} value={booth.pollingBoothId}>
                              {booth.pollingBoothId} - {booth.name} ({booth.district})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Upload Mode Toggle */}
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-emerald-800">अपलोड मोड</Label>
                      <div className="flex rounded-lg border border-emerald-200 overflow-hidden">
                        <button
                          className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-all ${
                            uploadMode === 'file'
                              ? 'bg-emerald-600 text-white'
                              : 'bg-white text-gray-600 hover:bg-emerald-50'
                          }`}
                          onClick={() => setUploadMode('file')}
                        >
                          <FileUp className="w-4 h-4" />
                          CSV फ़ाइल
                        </button>
                        <button
                          className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-all ${
                            uploadMode === 'json'
                              ? 'bg-emerald-600 text-white'
                              : 'bg-white text-gray-600 hover:bg-emerald-50'
                          }`}
                          onClick={() => setUploadMode('json')}
                        >
                          <ClipboardPaste className="w-4 h-4" />
                          JSON पेस्ट
                        </button>
                      </div>
                    </div>

                    {/* File Upload */}
                    {uploadMode === 'file' && (
                      <div className="space-y-3">
                        <div className="border-2 border-dashed border-emerald-200 rounded-lg p-8 text-center hover:border-emerald-400 transition-colors bg-emerald-50/30">
                          <FileUp className="w-10 h-10 mx-auto text-emerald-400 mb-3" />
                          <p className="text-sm font-medium text-emerald-800 mb-1">CSV फ़ाइल अपलोड करें</p>
                          <p className="text-xs text-gray-500 mb-3">
                            EPIC No, Serial No, Name, Name Hindi, Father/Husband Name, Relation Type, Age, Gender, Address, House No
                          </p>
                          <Input
                            type="file"
                            accept=".csv"
                            onChange={handleFileUpload}
                            disabled={uploading || !uploadBoothId}
                            className="max-w-xs mx-auto"
                          />
                        </div>
                      </div>
                    )}

                    {/* JSON Paste */}
                    {uploadMode === 'json' && (
                      <div className="space-y-3">
                        <Textarea
                          placeholder={`[\n  {\n    "epicNo": "ABC1234567",\n    "serialNo": 1,\n    "name": "Ram Kumar",\n    "nameHindi": "राम कुमार",\n    "fatherHusbandName": "Shyam Kumar",\n    "relationType": "पिता",\n    "age": 45,\n    "gender": "पुरुष",\n    "address": "ग्राम समरधा",\n    "houseNo": "12"\n  }\n]`}
                          value={jsonText}
                          onChange={(e) => setJsonText(e.target.value)}
                          disabled={uploading || !uploadBoothId}
                          className="min-h-[200px] font-mono text-sm"
                        />
                        <Button
                          className="bg-emerald-600 hover:bg-emerald-700 w-full"
                          onClick={handleJsonUpload}
                          disabled={uploading || !uploadBoothId}
                        >
                          {uploading ? (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              अपलोड हो रहा है...
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4 mr-2" />
                              JSON अपलोड करें
                            </>
                          )}
                        </Button>
                      </div>
                    )}

                    {/* Progress */}
                    {uploading && (
                      <div className="space-y-2">
                        <Progress value={uploadProgress} className="h-2" />
                        <p className="text-xs text-gray-500 text-center">अपलोड प्रगति: {uploadProgress}%</p>
                      </div>
                    )}

                    {/* Result */}
                    {uploadResult && (
                      <div
                        className={`flex items-center gap-3 p-4 rounded-lg ${
                          uploadResult.success ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'
                        }`}
                      >
                        {uploadResult.success ? (
                          <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                        ) : (
                          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
                        )}
                        <div>
                          <p className={`text-sm font-medium ${uploadResult.success ? 'text-emerald-800' : 'text-red-800'}`}>
                            {uploadResult.success ? 'अपलोड सफल!' : 'अपलोड विफल!'}
                          </p>
                          <p className={`text-xs ${uploadResult.success ? 'text-emerald-600' : 'text-red-600'}`}>
                            {uploadResult.message}
                          </p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Booth Voter Counts Sidebar */}
              <div className="lg:col-span-1">
                <Card className="border-emerald-100">
                  <CardHeader>
                    <CardTitle className="text-base text-emerald-900 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      बूथवार मतदाता संख्या
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {booths.length === 0 ? (
                      <div className="text-center py-8 text-gray-400">
                        <Building2 className="w-8 h-8 mx-auto mb-2" />
                        <p className="text-sm">कोई बूथ नहीं</p>
                      </div>
                    ) : (
                      <div className="max-h-96 overflow-y-auto">
                        {booths.map((booth) => (
                          <div
                            key={booth.id}
                            className={`flex items-center justify-between px-4 py-3 border-b last:border-b-0 cursor-pointer transition-colors ${
                              uploadBoothId === booth.pollingBoothId
                                ? 'bg-emerald-50 border-emerald-100'
                                : 'hover:bg-gray-50'
                            }`}
                            onClick={() => setUploadBoothId(booth.pollingBoothId)}
                          >
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium truncate">{booth.pollingBoothId}</p>
                              <p className="text-xs text-gray-500 truncate">{booth.name}</p>
                            </div>
                            <div className="text-right ml-3 flex-shrink-0">
                              <p className="text-sm font-bold text-emerald-700">
                                {booth.voterCount.toLocaleString('hi-IN')}
                              </p>
                              <p className="text-xs text-gray-400">मतदाता</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Upload Instructions */}
                <Card className="border-amber-200 bg-amber-50/50 mt-4">
                  <CardContent className="p-4">
                    <h4 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      निर्देश
                    </h4>
                    <ul className="text-xs text-amber-700 space-y-1.5 list-disc list-inside">
                      <li>CSV कॉलम: epicNo, serialNo, name, nameHindi, fatherHusbandName, relationType, age, gender, address, houseNo</li>
                      <li>पहली पंक्ति हेडर होनी चाहिए</li>
                      <li>डुप्लिकेट EPIC No. स्वचालित रूप से छोड़ दिए जाएंगे</li>
                      <li>JSON ऐरे फ़ॉर्मेट में सभी आवश्यक फ़ील्ड होने चाहिए</li>
                      <li>प्रत्येक मतदाता के लिए pollingBoothId स्वचालित रूप से सेट होगा</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* ── Section 3: Voter Data Table ── */}
            <Separator className="my-2" />
            <Card className="border-emerald-100">
              <CardHeader className="pb-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <CardTitle className="text-lg text-emerald-900 flex items-center gap-2">
                      <Eye className="w-5 h-5" />
                      मतदाता सूची देखें
                      <span className="text-xs font-normal text-gray-500">(Voter Data Table)</span>
                    </CardTitle>
                    <CardDescription className="text-sm text-gray-500 mt-1">
                      किसी बूथ का चयन करें और मतदाताओं की सूची देखें
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Booth Selection for Voter List + Search */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="w-full sm:w-72">
                    <Select value={voterListBoothId} onValueChange={setVoterListBoothId}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="मतदान कक्ष चुनें (सूची देखें)" />
                      </SelectTrigger>
                      <SelectContent>
                        {booths.map((booth) => (
                          <SelectItem key={booth.pollingBoothId} value={booth.pollingBoothId}>
                            {booth.pollingBoothId} - {booth.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {voterListBoothId && (
                    <div className="relative flex-1 max-w-md">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <Input
                        placeholder="सभी फ़ील्ड में खोजें... (EPIC, नाम, पता...)"
                        value={voterListSearch}
                        onChange={(e) => setVoterListSearch(e.target.value)}
                        className="pl-9 w-full"
                      />
                    </div>
                  )}
                </div>

                {/* No booth selected */}
                {!voterListBoothId && (
                  <div className="text-center py-10 text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
                    <Users className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm font-medium text-gray-500">मतदाता सूची देखने के लिए बूथ चुनें</p>
                    <p className="text-xs text-gray-400 mt-1">ऊपर ड्रॉपडाउन से कोई मतदान कक्ष चुनें</p>
                  </div>
                )}

                {/* Loading */}
                {voterListBoothId && voterListLoading && (
                  <div className="flex items-center justify-center py-10">
                    <RefreshCw className="w-6 h-6 text-emerald-600 animate-spin" />
                    <span className="ml-2 text-emerald-700">मतदाता सूची लोड हो रही है...</span>
                  </div>
                )}

                {/* Voter Table */}
                {voterListBoothId && !voterListLoading && voterListData.length > 0 && (
                  <>
                    {/* Info bar */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-emerald-50 rounded-lg px-4 py-2.5">
                      <p className="text-sm text-emerald-800">
                        <span className="font-semibold">कुल मतदाता:</span>{' '}
                        <span className="font-bold">{voterListTotal.toLocaleString('hi-IN')}</span>
                      </p>
                      <p className="text-xs text-emerald-600">
                        पृष्ठ {voterListPage} / {voterListTotalPages || 1}
                        {voterListSearch && <span className="ml-2">| खोज: &quot;{voterListSearch}&quot;</span>}
                      </p>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto border border-emerald-100 rounded-lg">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-emerald-50 hover:bg-emerald-50">
                            <TableHead className="font-semibold text-emerald-900 w-12 text-center">क्र.</TableHead>
                            <TableHead className="font-semibold text-emerald-900">EPiC नंबर</TableHead>
                            <TableHead className="font-semibold text-emerald-900">नाम</TableHead>
                            <TableHead className="font-semibold text-emerald-900">पिता/पति</TableHead>
                            <TableHead className="font-semibold text-emerald-900 text-center">लिंग</TableHead>
                            <TableHead className="font-semibold text-emerald-900 text-center">आयु</TableHead>
                            <TableHead className="font-semibold text-emerald-900">पता</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {voterListData.map((voter) => (
                            <TableRow key={voter.id} className="hover:bg-emerald-50/50">
                              <TableCell className="text-center font-mono text-sm text-gray-600">
                                {voter.serialNo}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className="font-mono text-xs border-emerald-300 text-emerald-700">
                                  {voter.epicNo}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <p className="font-medium text-sm">{voter.nameHindi || voter.name}</p>
                                {voter.nameHindi && voter.name !== voter.nameHindi && (
                                  <p className="text-xs text-gray-400 mt-0.5">{voter.name}</p>
                                )}
                              </TableCell>
                              <TableCell>
                                <p className="text-sm text-gray-700">{voter.fatherHusbandName}</p>
                                {voter.relationType && (
                                  <p className="text-xs text-gray-400 mt-0.5">({voter.relationType})</p>
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge
                                  className={`text-xs ${
                                    genderInHindi(voter.gender) === 'पुरुष'
                                      ? 'bg-blue-50 text-blue-700 hover:bg-blue-50 border border-blue-200'
                                      : genderInHindi(voter.gender) === 'महिला'
                                        ? 'bg-pink-50 text-pink-700 hover:bg-pink-50 border border-pink-200'
                                        : 'bg-gray-50 text-gray-600 hover:bg-gray-50 border border-gray-200'
                                  }`}
                                  variant="outline"
                                >
                                  {genderInHindi(voter.gender)}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-center text-sm text-gray-600">
                                {voter.age || '-'}
                              </TableCell>
                              <TableCell className="text-sm text-gray-600 max-w-[200px]">
                                <p className="truncate">{voter.address || '-'}</p>
                                {voter.houseNo && (
                                  <p className="text-xs text-gray-400 mt-0.5">मकान नं. {voter.houseNo}</p>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Pagination */}
                    {voterListTotalPages > 1 && (
                      <div className="flex items-center justify-between gap-2 pt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs"
                          disabled={voterListPage <= 1}
                          onClick={() => handleVoterListPageChange(voterListPage - 1)}
                        >
                          <ChevronLeft className="w-4 h-4 mr-1" />
                          पिछला
                        </Button>

                        <div className="flex items-center gap-1">
                          {getVoterListPageNumbers().map((page, idx) =>
                            typeof page === 'string' ? (
                              <span key={`ellipsis-${idx}`} className="px-2 text-gray-400 text-sm">
                                ...
                              </span>
                            ) : (
                              <Button
                                key={page}
                                variant={page === voterListPage ? 'default' : 'outline'}
                                size="sm"
                                className={`h-8 w-8 p-0 text-xs ${
                                  page === voterListPage
                                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                                    : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'
                                }`}
                                onClick={() => handleVoterListPageChange(page)}
                              >
                                {page}
                              </Button>
                            )
                          )}
                        </div>

                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs"
                          disabled={voterListPage >= voterListTotalPages}
                          onClick={() => handleVoterListPageChange(voterListPage + 1)}
                        >
                          अगला
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    )}
                  </>
                )}

                {/* Empty state (booth selected but no voters) */}
                {voterListBoothId && !voterListLoading && voterListData.length === 0 && (
                  <div className="text-center py-10 text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
                    <Users className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm font-medium text-gray-500">
                      {voterListSearch
                        ? `&quot;${voterListSearch}&quot; के लिए कोई मतदाता नहीं मिला`
                        : 'इस बूथ में कोई मतदाता डेटा नहीं है'}
                    </p>
                    {!voterListSearch && (
                      <p className="text-xs text-gray-400 mt-1">ऊपर अपलोड सेक्शन से मतदाता डेटा अपलोड करें</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        {/* ═══════ TAB 4: Sync Settings ═══════ */}
        <TabsContent value="sync-settings">
          <div className="space-y-6">
            {/* Section 1: Global Sync Settings */}
            <Card className="border-emerald-100">
              <CardHeader>
                <CardTitle className="text-lg text-emerald-900 flex items-center gap-2">
                  <RefreshCw className="w-5 h-5" />
                  ग्लोबल सिंक सेटिंग्स
                  <span className="text-xs font-normal text-gray-500">(Global Sync Settings)</span>
                </CardTitle>
                <CardDescription>
                  सभी बूथों पर लागू होने वाली डिफ़ॉल्ट सिंक सेटिंग्स प्रबंधित करें
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {syncSettingsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 text-emerald-600 animate-spin" />
                    <span className="ml-2 text-emerald-700">लोड हो रहा है...</span>
                  </div>
                ) : (
                  <>
                    {/* Sync Enabled Toggle */}
                    <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                      <div className="flex-1">
                        <h3 className="font-semibold text-emerald-900">सिंक सक्षम (Sync Enabled)</h3>
                        <p className="text-sm text-emerald-700 mt-0.5">
                          सभी बूथों के लिए सिंक को चालू या बंद करें। बंद करने पर कोई भी बूथ सिंक नहीं करेगा।
                        </p>
                      </div>
                      <Switch
                        checked={globalSyncEnabled}
                        onCheckedChange={setGlobalSyncEnabled}
                        className="data-[state=checked]:bg-emerald-600"
                      />
                    </div>

                    {/* Sync Frequency */}
                    <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900">सिंक आवृत्ति (Sync Frequency)</h3>
                          <p className="text-sm text-gray-600 mt-0.5">कितने मिनट में सिंक होगा (1-1440 मिनट)</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min={1}
                            max={1440}
                            value={globalSyncFreq}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || 1;
                              setGlobalSyncFreq(Math.min(1440, Math.max(1, val)));
                            }}
                            className="w-20 text-center font-bold text-emerald-700"
                          />
                          <span className="text-sm text-gray-500">मिनट</span>
                        </div>
                      </div>

                      {/* Slider */}
                      <Slider
                        value={[globalSyncFreq]}
                        onValueChange={(value) => setGlobalSyncFreq(value[0])}
                        min={1}
                        max={1440}
                        step={1}
                        className="w-full"
                      />

                      {/* Preset Buttons */}
                      <div className="flex flex-wrap gap-2">
                        {[5, 15, 30, 60, 120, 360, 720, 1440].map((preset) => (
                          <Button
                            key={preset}
                            variant={globalSyncFreq === preset ? 'default' : 'outline'}
                            size="sm"
                            className={globalSyncFreq === preset
                              ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                              : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                            }
                            onClick={() => setGlobalSyncFreq(preset)}
                          >
                            {preset < 60 ? `${preset} मिनट` : preset === 60 ? '1 घंटा' : preset === 120 ? '2 घंटे' : preset === 360 ? '6 घंटे' : preset === 720 ? '12 घंटे' : '24 घंटे'}
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Current Effective Settings Display */}
                    <div className="flex items-center gap-3 p-3 bg-emerald-50/50 rounded-lg border border-emerald-100 text-sm">
                      <Info className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                      <div className="text-emerald-800">
                        <span className="font-medium">वर्तमान सेटिंग्स:</span>{' '}
                        सिंक {globalSyncEnabled ? (
                          <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 ml-1">सक्षम</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-800 hover:bg-red-100 ml-1">अक्षम</Badge>
                        )}
                        {' '}&middot; आवृत्ति: <span className="font-bold">{globalSyncFreq} मिनट</span>
                        {globalSyncFreq >= 60 && <span className="text-emerald-600 ml-1">({(globalSyncFreq / 60).toFixed(1)} घंटे)</span>}
                      </div>
                    </div>

                    {/* Save Button */}
                    <div className="flex justify-end">
                      <Button
                        className="bg-emerald-600 hover:bg-emerald-700"
                        onClick={saveGlobalSyncSettings}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        ग्लोबल सेटिंग्स सहेजें
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Section 2: Booth-Level Sync Settings */}
            <Card className="border-emerald-100">
              <CardHeader>
                <CardTitle className="text-lg text-emerald-900 flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  बूथ-स्तरीय सिंक सेटिंग्स
                  <span className="text-xs font-normal text-gray-500">(Booth-Level Sync Settings)</span>
                </CardTitle>
                <CardDescription>
                  प्रत्येक बूथ के लिए सिंक को अलग से चालू/बंद करें या कस्टम आवृत्ति सेट करें
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {booths.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium">कोई मतदान कक्ष नहीं मिला</p>
                    <p className="text-sm">पहले मतदान कक्ष जोड़ें</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-emerald-50 hover:bg-emerald-50">
                          <TableHead className="font-semibold text-emerald-900">बूथ ID</TableHead>
                          <TableHead className="font-semibold text-emerald-900">नाम</TableHead>
                          <TableHead className="font-semibold text-emerald-900 text-center">सिंक सक्षम</TableHead>
                          <TableHead className="font-semibold text-emerald-900 text-center">प्रभावी आवृत्ति</TableHead>
                          <TableHead className="font-semibold text-emerald-900 text-right">कार्रवाई</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {booths.map((booth) => {
                          const override = boothSyncOverrides[booth.pollingBoothId];
                          const isEnabled = override?.enabled ?? globalSyncEnabled;
                          const effectiveFreq = override?.freq ?? globalSyncFreq;
                          const isEditing = editingBoothSync === booth.pollingBoothId;

                          return (
                            <TableRow key={booth.id} className="hover:bg-emerald-50/50">
                              <TableCell>
                                <Badge variant="outline" className="font-mono text-xs border-emerald-300 text-emerald-700">
                                  {booth.pollingBoothId}
                                </Badge>
                              </TableCell>
                              <TableCell className="font-medium max-w-[200px] truncate">{booth.name}</TableCell>
                              <TableCell className="text-center">
                                <Switch
                                  checked={isEnabled}
                                  onCheckedChange={(checked) => {
                                    saveBoothSyncSettings(booth.pollingBoothId, checked, effectiveFreq);
                                  }}
                                  className="data-[state=checked]:bg-emerald-600"
                                />
                              </TableCell>
                              <TableCell className="text-center">
                                {isEditing ? (
                                  <div className="flex items-center justify-center gap-2">
                                    <Input
                                      type="number"
                                      min={1}
                                      max={1440}
                                      value={effectiveFreq}
                                      onChange={(e) => {
                                        const val = parseInt(e.target.value) || 1;
                                        setBoothSyncOverrides((prev) => ({
                                          ...prev,
                                          [booth.pollingBoothId]: {
                                            enabled: isEnabled,
                                            freq: Math.min(1440, Math.max(1, val)),
                                          },
                                        }));
                                      }}
                                      className="w-20 text-center text-sm h-8"
                                    />
                                    <span className="text-xs text-gray-500">मि.</span>
                                  </div>
                                ) : (
                                  <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100">
                                    {effectiveFreq} मिनट
                                    {effectiveFreq >= 60 && <span className="ml-1 text-gray-500">({(effectiveFreq / 60).toFixed(1)} घंटे)</span>}
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  {isEditing ? (
                                    <>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 px-2 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50"
                                        onClick={() => {
                                          saveBoothSyncSettings(booth.pollingBoothId, isEnabled, effectiveFreq);
                                          setEditingBoothSync(null);
                                        }}
                                        title="सहेजें"
                                      >
                                        <CheckCircle2 className="w-4 h-4 mr-1" />
                                        <span className="text-xs">सहेजें</span>
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 px-2 text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                                        onClick={() => {
                                          setEditingBoothSync(null);
                                          fetchSyncSettings();
                                        }}
                                        title="रद्द करें"
                                      >
                                        <span className="text-xs">रद्द</span>
                                      </Button>
                                    </>
                                  ) : (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50"
                                      onClick={() => setEditingBoothSync(booth.pollingBoothId)}
                                      title="आवृत्ति संपादित करें"
                                    >
                                      <Pencil className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
